// Shop hours model + open/closed logic shared by the owner editor and the
// client-facing booking surfaces. Times are stored per weekday as TIME strings
// ("09:00:00"). Open/closed is evaluated in the viewer's local time — for an
// in-shop tablet or a nearby client that matches shop-local closely enough for
// a "we're open / opens back at X" sign (no timezone column needed for v1).

export type ShopHour = {
  day_of_week: number // 0=Sunday … 6=Saturday
  is_closed: boolean
  open_time: string | null
  close_time: string | null
}

export const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
export const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// "09:00" / "09:00:00" → minutes since midnight. Returns null on bad input.
function toMinutes(t: string | null): number | null {
  if (!t) return null
  const [h, m] = t.split(":")
  const hh = Number(h)
  const mm = Number(m ?? 0)
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null
  return hh * 60 + mm
}

// "09:00:00" → "9:00 AM"
export function formatTime(t: string | null): string {
  const min = toMinutes(t)
  if (min == null) return ""
  let h = Math.floor(min / 60)
  const m = min % 60
  const ampm = h >= 12 ? "PM" : "AM"
  h = h % 12
  if (h === 0) h = 12
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`
}

export type OpenState = {
  open: boolean
  /** When open: e.g. "Open until 7:00 PM". */
  closesAtLabel?: string
  /** When closed: e.g. "Back at 9:00 AM" / "Opens Wed 9:00 AM". */
  nextOpenLabel?: string
}

// Resolve the current open/closed state from a shop's weekly hours.
// Returns null when no hours are configured (callers can then show nothing).
export function getOpenState(hours: ShopHour[] | null | undefined, now: Date = new Date()): OpenState | null {
  if (!hours || hours.length === 0) return null
  const byDay = new Map(hours.map((h) => [h.day_of_week, h]))
  const dow = now.getDay()
  const nowMin = now.getHours() * 60 + now.getMinutes()

  const today = byDay.get(dow)
  if (today && !today.is_closed) {
    const open = toMinutes(today.open_time)
    const close = toMinutes(today.close_time)
    if (open != null && close != null) {
      if (nowMin >= open && nowMin < close) {
        return { open: true, closesAtLabel: `Open until ${formatTime(today.close_time)}` }
      }
      if (nowMin < open) {
        return { open: false, nextOpenLabel: `Back at ${formatTime(today.open_time)}` }
      }
    }
  }

  // Closed for the rest of today — find the next day that opens.
  for (let i = 1; i <= 7; i++) {
    const d = (dow + i) % 7
    const h = byDay.get(d)
    if (h && !h.is_closed && toMinutes(h.open_time) != null) {
      const when = i === 1 ? "tomorrow" : DAY_SHORT[d]
      return { open: false, nextOpenLabel: `Opens ${when} ${formatTime(h.open_time)}` }
    }
  }

  // No upcoming open day configured.
  return { open: false }
}

// A compact one-line summary of today's hours, e.g. "Today 9:00 AM – 7:00 PM".
export function todayHoursLabel(hours: ShopHour[] | null | undefined, now: Date = new Date()): string | null {
  if (!hours || hours.length === 0) return null
  const today = hours.find((h) => h.day_of_week === now.getDay())
  if (!today || today.is_closed || !today.open_time || !today.close_time) return "Closed today"
  return `Today ${formatTime(today.open_time)} – ${formatTime(today.close_time)}`
}
